import { injectable } from 'inversify';
import { plainToClass } from 'class-transformer';

import { HttpService } from '@vk-hr-tek/core/http';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { Filter, FilterService, FilterType } from '@vk-hr-tek/core/filter';

import {
  GetEventFileHashesResponse,
  BatchEventCancelResponse,
  CancelReasonListResponse,
  EventListRequest,
  BatchEventOperationsInfoResponse,
} from '@app/gen/events';

import { GetHashesDto, CancelBatchEventsDto, GetEventsDto } from '../dto';

@injectable()
export class EventsActionsService {
  constructor(
    private validator: ValidationService,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private filterService: FilterService,
  ) {}

  async getCancelReasons({ isCandidate }: { isCandidate?: boolean }) {
    const result = await this.unmarshaller.unmarshall(
      await this.http.get(
        '/event/cancel_reasons',
        isCandidate ? { is_candidate: isCandidate } : {},
        { withSide: true },
      ),
      CancelReasonListResponse,
    );

    return result.reasons;
  }

  async getHashes(getHashesDto: GetHashesDto) {
    const result = await this.unmarshaller.unmarshall(
      await this.http.post(`/event/batch/document/hash`, getHashesDto, {
        withSide: true,
      }),
      GetEventFileHashesResponse,
    );
    return result as GetEventFileHashesResponse;
  }

  async sendBatchCancel(sendBatchCancelDto: CancelBatchEventsDto) {
    await this.validator.validateOrReject(
      sendBatchCancelDto,
      CancelBatchEventsDto,
    );

    const { events, reasonId, comment } = sendBatchCancelDto;

    const result = await this.unmarshaller.unmarshall(
      this.http.post(
        '/event/batch/cancel',
        {
          events,
          reason_id: reasonId,
          comment,
        },
        {
          withSide: true,
          reloadIfOldVersion: true,
        },
      ),
      BatchEventCancelResponse,
    );

    return {
      successes: result.successes || [],
      errors: result.errors || [],
    };
  }

  async getBatchEventOperationsInfo({
    getEventDto = {},
    availableFilters,
  }: {
    getEventDto?: EventListRequest;
    availableFilters: Filter[];
  }) {
    const eventsDto = plainToClass(
      this.filterService.buildDto(GetEventsDto, availableFilters),
      getEventDto,
    );

    await this.validator.validateOrReject(eventsDto);

    const filters: FilterType = eventsDto.filters;

    const result = await this.http.post(
      '/event/batch_operations/info',
      {
        ...getEventDto,
        filters: filters,
      },
      {
        withSide: true,
      },
    );

    const response = await this.unmarshaller.unmarshall(
      result,
      BatchEventOperationsInfoResponse,
    );

    return response;
  }
}
